<template>
    <section>
        <div class="container-fluid px-2 py-0">
            <x-panel margin>
                <div slot="title" class="d-flex align-items-center mx-0">
                    <!-- create new application -->
                    <div v-if="canAssignApp || isBroker">
                        <el-button
                            size="small"
                            class="mr-2"
                            type="success"
                            @click.stop="$bus.$emit('toggle-application-journey')"
                        >
                            Create Application
                        </el-button>
                    </div>

                    <div v-else-if="isReferrer || Ambition.User.isAcrReferrer">
                        <el-button size="small" class="mr-2" type="success"
                                   @click.stop="$bus.$emit('create-opp-remote', {})">
                            Create New Referral
                        </el-button>
                    </div>

                    <div v-else-if="Ambition.User.isNonAcrReferrer">
                        <el-button size="small" class="mr-2" type="success" @click.stop="createApplication">
                            Create Application
                        </el-button>
                    </div>

                    <!-- filter by assigned status -->
                    <div class="m-2" v-if="canAssignApp || canViewAllApps">
                        <el-select v-model="searchOptions.filter" @change="filter" reserve-keyword size="small">
                            <el-option label="Assigned Applications" value="assigned"></el-option>
                            <el-option label="Unassigned Applications" value="unassigned"></el-option>
                            <el-option label="All Applications" value="all"></el-option>
                            <el-option label="My Applications" value="self"></el-option>
                            <el-option v-if="canMakeInsuranceDeal" label="Insurance Deals"
                                       value="insurance"></el-option>
                        </el-select>
                    </div>
                    <div v-else>
                        <h3 class="mr-2">{{ isReferrer ? `Applications` : `My Applications` }}</h3>
                    </div>

                    <!-- filter by team(s) -->
                    <div class="m-2" v-if="Ambition.Globals.tenant.user.teams.length > 1">
                        <el-select v-model="searchOptions.team" @change="filter" reserve-keyword size="small">
                            <el-option label="All Teams" value="all"></el-option>
                            <el-option
                                v-for="team in Ambition.Globals.tenant.user.teams"
                                :key="team.id"
                                :label="team.name"
                                :value="team.id"
                            ></el-option>
                        </el-select>
                    </div>
                </div>

                <div slot="extra" class="d-flex align-items-center mx-0">
                    <el-button class="mr-2" type="primary" @click="toggleFilters()" size="small" icon="fas fa-filter">
                        Filters / Sort
                    </el-button>

                    <el-input
                        v-model="search"
                        placeholder="Search Filter..."
                        clearable
                        style="width: 200px"
                        size="small"
                        @clear="handleClear"
                    />

                    <el-select
                        v-if="!_.isEmpty(savedFilterArray)"
                        class="ml-2"
                        clearable
                        placeholder="Select Saved Filter"
                        style="width: 180px"
                        size="small"
                        v-model="selectedSavedFilter"
                        @change="handleSelectedSavedFilter"
                        @clear="clearSelectedSavedFilter"
                    >
                        <el-option v-for="(view, index) in savedFilterArray" :key="index" :label="index"
                                   :value="index"/>
                    </el-select>

                    <el-dropdown class="m-2" v-if="canAssignApp" @command="assign" trigger="click" size="small">
                        <span class="el-dropdown-link">
                            <el-badge :hidden="selectedNumber == 0" :value="selectedNumber" class="item">
                                <el-button type="success" size="small">Assign Selected</el-button>
                            </el-badge>
                        </span>
                        <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item v-for="user in assignUserFilter" :key="user.id" :command="user">
                                {{ user.first_name }} {{ user.last_name }}
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                    <el-badge v-if="canAssignApp" :hidden="selectedNumber == 0" :value="selectedNumber" class="item">
                        <el-button
                            type="danger"
                            :disabled="selectedNumber ? false : true"
                            @click="closeApplications"
                            size="small"
                        >
                            Close Selected
                        </el-button>
                    </el-badge>

                    <el-select
                        v-if="!isReferrer"
                        class="ml-2"
                        :value="viewAs"
                        placeholder="Select View"
                        style="width: 180px"
                        size="small"
                        @change="handleViewChange"
                    >
                        <el-option v-for="(view, index) in viewAsOptionsFiltered" :key="view" :label="index"
                                   :value="view"/>
                    </el-select>
                    <el-button
                        class="ml-2"
                        type="default"
                        icon="fas fa-cog"
                        size="small"
                        v-if="applicationTabView == 'list'"
                        @click="$bus.$emit('applicationTab::open::tableSettings', true)"
                    ></el-button>
                    <el-dropdown style="width: 110px;" :loading="true" class="ml-2" size="small" split-button
                                 type="primary" @click="getApplications" @command="setRefreshTimeout">
                        <i v-if="isFetchingApps" class="el-icon-loading"></i>
                        <i v-else class="el-icon-refresh"></i>

                        <div v-if="appRefreshSeconds" style="width:30px; display: inline-block;">
                            {{ convertMsToMinutesSeconds(appRefreshSeconds * 1000) }}
                        </div>

                        <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item :command="0">Off</el-dropdown-item>
                            <el-dropdown-item :command="120000">2mins</el-dropdown-item>
                            <el-dropdown-item :command="300000">5mins</el-dropdown-item>
                            <el-dropdown-item :command="600000">10mins</el-dropdown-item>
                            <el-dropdown-item :command="900000">15mins</el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>

                </div>
            </x-panel>
            <transition name="slide">
                <x-panel margin v-show="showFilters">
                    <div slot="title" class="d-flex align-items-center mx-0">
                        <h3>Filter By:</h3>
                        <!-- filter by application type -->
                        <div class="m-2">
                            <el-select
                                v-model="searchOptions.applicationType"
                                class=""
                                clearable
                                @change="filter"
                                @clear="filter"
                                :loading="loading"
                                reserve-keyword
                                placeholder="Filter By Type"
                                size="small"
                            >
                                <el-option-group label="Application Type">
                                    <el-option
                                        v-for="item in applicationTypesFilter"
                                        :key="item.id"
                                        :label="item.name"
                                        :value="item.id"
                                    ></el-option>
                                </el-option-group>
                            </el-select>
                        </div>

                        <!-- filter by assigned user -->
                        <div class="m-2" v-if="(canAssignApp && !isReferrer) || canViewAllApps">
                            <el-select
                                style="width: 100%"
                                v-model="searchOptions.assignedUser"
                                clearable
                                filterable
                                placeholder="Filter by Assigned"
                                :loading="_.isEmpty(brokerList)"
                                @change="filter"
                                @clear="filter"
                                size="small"
                            >
                                <el-option-group v-for="group in brokerList" :key="group.label" :label="group.label">
                                    <el-option v-for="item in group.options" :key="item.value" :label="item.label"
                                               :value="item.value"></el-option>
                                </el-option-group>
                            </el-select>
                        </div>

                        <!-- filter by referrer -->
                        <div class="m-2" v-if="canAssignApp || isBroker || isReferrer || canViewAllApps">
                            <!-- filter by refferer group -->
                            <el-select
                                v-if="!isReferrer"
                                v-model="searchOptions.referrer"
                                class="mr-2"
                                remote
                                clearable
                                @change="changedReferrer"
                                @clear="filter"
                                @focus="getReferrersInital"
                                :loading="loading"
                                :remote-method="getReferrers"
                                reserve-keyword
                                placeholder="Filter By Referrer"
                                size="small"
                            >
                                <el-option-group label="Referrers">
                                    <el-option
                                        v-for="item in assignReferrersInitialFilter"
                                        :key="item.id"
                                        :label="`${item.name}`"
                                        :value="item.id"
                                    ></el-option>
                                </el-option-group>
                            </el-select>

                            <!-- filter by referrer user -->
                            <el-select
                                v-model="searchOptions.referrer_user"
                                v-show="searchOptions.referrer || referrerId"
                                class="ml-2"
                                remote
                                clearable
                                @change="filter"
                                @clear="filter"
                                :loading="loading"
                                :remote-method="getReferrerUsers"
                                reserve-keyword
                                placeholder="Filter By Referrer User"
                                size="small"
                            >
                                <el-option-group label="Referrer Users">
                                    <el-option
                                        v-for="item in assignReferrerUsersInitial"
                                        :key="item.id"
                                        :label="`${item.first_name} ${item.last_name}`"
                                        :value="item.id"
                                    ></el-option>
                                </el-option-group>
                            </el-select>
                        </div>

                        <!-- filter by category -->
                        <div class="mr-2" v-if="(canAssignApp && !isReferrer) || canViewAllApps || isBroker">
                            <el-select
                                v-model="searchOptions.application_categories"
                                class=""
                                style="width: 300px"
                                clearable
                                multiple
                                collapse-tags
                                @change="filter"
                                @clear="filter"
                                :loading="loading"
                                reserve-keyword
                                placeholder="Filter By Category"
                                size="small"
                            >
                                <el-option-group label="Categories" class="category-select">
                                    <el-option
                                        v-for="item in applicationCategories"
                                        :key="item.uid"
                                        :value="item.uid"
                                        :label="item.name"
                                    >
                                        <span style="float: left">{{ item.name }}</span>
                                        <span class="category-item" style="float: right">
                                            <div :style="`background: ${item.colour}`" class="category-dot"></div>
                                        </span>
                                    </el-option>
                                </el-option-group>
                            </el-select>
                        </div>

                        <div class="m-2" v-if="isReferrer">
                            <el-checkbox
                                v-model="filterShowCompleted"
                                @change="getCompleted"
                                size="small"
                                label="Completed Apps"
                                @clear="filter"
                            >
                            </el-checkbox>

                            <el-checkbox
                                v-model="closedApplications"
                                @change="getClosed"
                                @clear="filter"
                                size="small"
                                label="Closed Apps"
                            >
                            </el-checkbox>
                        </div>

                    </div>

                    <div slot="extra" class="d-flex align-items-center mx-0" v-if="applicationTabView != 'list'">
                        <h3>Sort By:</h3>
                        <div class="m-2">
                            <el-select v-model="sortby.prop" clearable placeholder="Sort Property" value-key="name"
                                       size="small">
                                <el-option
                                    v-for="(sort, index) in sortOptions"
                                    :key="index"
                                    :label="sort.title"
                                    :value="sort.value"
                                ></el-option>
                            </el-select>
                        </div>
                        <el-select v-model="sortby.order" class="" clearable placeholder="Sort Direction" size="small">
                            <el-option
                                v-for="(type, index) in sortOptionsDirections"
                                :key="index"
                                :label="type.label"
                                :value="type.type"
                            ></el-option>
                        </el-select>
                    </div>

                    <div class="d-flex align-items-center mx-0" v-if="applicationTabView != 'list'">
                        <div class="m-2">
                            <el-input
                                v-model="filterName"
                                placeholder="Filter Name..."
                                clearable
                                size="small"
                                @clear="clearSaveFilterInput"
                            />

                            <el-alert
                                v-if="checkFilterName"
                                type="error"
                                :description="'Filter name already exists...'"
                                show-icon
                            ></el-alert>
                        </div>
                        <el-button size="small" class="mr-2" type="success" @click.stop="saveFilter"
                                   :disabled="checkFilterName || filterName === ''">
                            Save Filter
                        </el-button>
                        <div class="m-2" v-if="isReferrer">
                            <el-date-picker
                                style="max-width: 310px;"
                                class="col"
                                v-model="dateRangeArray"
                                type="daterange"
                                align="right"
                                unlink-panels
                                format="MMM dd, yyyy"
                                range-separator="-"
                                start-placeholder="Start date"
                                end-placeholder="End date"
                                @change="getDate"
                                :picker-options="pickerOptions"
                                :loading="loading"
                            >
                            </el-date-picker>
                        </div>
                    </div>
                </x-panel>
            </transition>
        </div>

        <!-- Kanban View -->
        <ApplicationsKanbanBoardView
            ref="kanbanView"
            v-if="(!isReferrer && applicationTabView == 'kanban') || (!isReferrer && applicationTabView == 'compact')"
            :key="applicationTabView"
            v-loading="loading"
            element-loading-text="Fetching applications..."
            element-loading-background="rgba(255, 255, 255, 0.3)"
            :stages="statuses"
            :applications="mappedApplications"
            :config="config"
            @create-note="createNote"
            @update-stage="updateStage"
            v-bind="{
                tokenInitial,
                isBroker,
                viewUnassigned,
                closeReasons,
                canAssignApp,
                canViewAllApps,
                isReferrer,
                referrerUserId,
                referrerId,
                canMakeInsuranceDeal,
                isIgnition,
                icons,
                sortby,
            }"
        ></ApplicationsKanbanBoardView>

        <!-- Table/List View -->
        <ApplicationsTableViewComponent
            ref="tableView"
            name="float-fade-in"
            v-if="isReferrer || applicationTabView == 'list'"
            v-loading="loading"
            element-loading-text="Fetching applications..."
            element-loading-background="rgba(255, 255, 255, 0.3)"
            :applications="allApplications"
            :config="config"
            :stages="statuses"
            @loading="
                (payload) => {
                    this.loading = payload
                }
            "
            @create-note="createNote"
            v-bind="{
                tokenInitial,
                isBroker,
                viewUnassigned,
                closeReasons,
                canAssignApp,
                canViewAllApps,
                isReferrer,
                referrerUserId,
                referrerId,
                canMakeInsuranceDeal,
                totalApps,
                search,
                isIgnition,
                icons,
            }"
        ></ApplicationsTableViewComponent>

        <!-- modals -->
        <edit-modal-component :token-initial="tokenInitial"></edit-modal-component>

        <!-- start add note modal -->
        <notes-modal
            :visible.sync="openNoteModal"
            :is-referrer="isReferrer"
            :application="noteApplication"
            :token="tokenInitial"
        ></notes-modal>
        <!-- end add note modal -->

        <!-- start close app modal -->
        <close-application-modal
            :visible.sync="closeApplicationModalVisibility"
            :application-ids="checked"
        ></close-application-modal>

        <application-modal v-if="closeModal === true">
            <div class="amb-modal-header py-1 d-flex justify-content-between">
                <div class="d-flex px-3 align-items-center">
                    <h2 class="modal-title mb-0 mr-3">Close Applications</h2>
                </div>
                <div class="p-3 amb-close-modal" v-on:click="closeModal = false">
                    <i class="fas fa-lg fa-times text-white"></i>
                </div>
            </div>
            <div class="container py-4">
                <div class="row pb-3 amb-modal-body">
                    <div class="col-12">
                        <p class="mb-0">
                            <strong>Reason for Closing</strong>
                        </p>
                        <application-select
                            class="select-belt"
                            :options="closeReasonOptions"
                            v-model="closeReason"
                            default-option
                            default-label="Select Close Reason"
                        ></application-select>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 d-flex justify-content-center align-items-center">
                        <button class="btn-light-blue w-50 mr-2" v-on:click="closeModal = false">Cancel</button>
                        <button class="btn-red w-50 ml-2" v-on:click="deleteSelected">Close</button>
                    </div>
                </div>
            </div>
        </application-modal>
        <!-- end close app modal -->
    </section>
</template>

<script>
import {mapGetters} from "vuex"
import {Message, Popover} from "element-ui"
import closeApplicationModal from "@ambition/components/closeApplication/closeApplicationModal"
import NotesModal from "@shared/components/modal/NotesModal"
import moment from 'moment'

export default {
    name: "ApplicationsComponent",
    components: {
        closeApplicationModal,
        NotesModal,
    },
    props: [
        "tenant",
        "config",
        "tokenInitial",
        "isBroker",
        "closeReasons",
        "isReferrer",
        "referrerUserId",
        "referrerId",
        "canMakeInsuranceDeal",
        "userSettings",
        "dropDownOptions",
    ],
    data() {
        return {
            closedApplications: false,
            brokerList: [],
            isFetchingApps: false,
            appRefreshTimeout: 0,
            appRefreshSeconds: 0,
            countDownTimerId: null,
            autoRefreshInterval: null,
            assignTasks: false,
            closeApplicationModalVisibility: false,
            ambitionSockets: null,
            viewAsOptions: {
                "Card View": "kanban",
                "Compact View": "compact",
                "List View": "list",
            },
            showFilters: false,
            isIgnition: Ambition.isIgnition(),
            applications: {},
            allApplications: [],
            closeModal: false,
            noteLoading: false,
            applicationTypes: [
                {
                    name: "Consumer",
                    id: 1,
                },
                {
                    name: "Commercial",
                    id: 2,
                },
                {
                    name: "Insurance",
                    id: 3,
                },
            ],
            statuses: [
                {
                    label: "Leads",
                    value: "1",
                    hide: false,
                    moveCardsTo: null,
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "",
                },
                {
                    label: "Application Taken",
                    value: "2",
                    hide: false,
                    moveCardsTo: null,
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "",
                },
                {
                    label: "Documents Received",
                    value: "3",
                    hide: true,
                    moveCardsTo: "4",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/conditioning",
                },
                {
                    label: "Submitted to Lender",
                    value: "4",
                    hide: false,
                    moveCardsTo: null,
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/loan-submission",
                },
                {
                    label: "Conditionally Approved",
                    value: "5",
                    hide: false,
                    moveCardsTo: null,
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/asset-and-seller",
                },
                {
                    label: "Invoice Requested",
                    value: "6",
                    hide: true,
                    moveCardsTo: "5",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/asset-and-seller",
                },
                {
                    label: "Invoice Returned",
                    value: "7",
                    hide: true,
                    moveCardsTo: "5",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/asset-and-seller",
                },
                {
                    label: "Loan Package Sent",
                    value: "8",
                    hide: true,
                    moveCardsTo: "5",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/loan-options",
                },
                {
                    label: "Loan Package Confirmed",
                    value: "9",
                    hide: true,
                    moveCardsTo: "5",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/loan-options",
                },
                {
                    depreciated: true,
                    label: "Loan Submitted",
                    value: "10",
                    hide: true,
                    moveCardsTo: "5",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/send-and-settle",
                },
                {
                    label: "Loan Reapproved",
                    value: "11",
                    hide: true,
                    moveCardsTo: "5",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/send-and-settle",
                },
                {
                    label: "Contracts Sent",
                    value: "12",
                    hide: true,
                    moveCardsTo: "5",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/send-and-settle",
                },
                {
                    label: "In for Settlement",
                    value: "13",
                    hide: true,
                    moveCardsTo: "5",
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/loan-summary",
                },
                {
                    label: "Settled",
                    value: "14",
                    hide: false,
                    moveCardsTo: null,
                    page: 1,
                    perPage: 20,
                    busy: false,
                    stageUrl: "/stage/loan-summary",
                },
            ],
            searchOptions: {
                filter: this.isBroker ? "self" : "all",
                status: "",
                applicationType: "",
                assignedUser: "",
                referrer: "",
                referrer_user: this.isReferrer ? this.referrerUserId : "",
                team: "all",
                application_categories: [],
                dateRange: "all",
            },
            closeReasonOptions: [],
            closeReason: "",
            assignUser: null,
            assignUsersInitial: [],
            assignReferrersInitial: [],
            assignReferrerUsersInitial: [],
            loading: false,
            assignUserSearchInput: "",
            openNoteModal: false,
            noteApplication: "",
            appNotes: [],
            usersViewingApp: {},
            search: "",
            filterName: "",
            checkFilterName: false,
            filterShowCompleted: false,
            selectedSavedFilter: "",
            savedFilterArray: [],
            icons: {
                Personal:
                    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDIuMSAzMDIuMSI+PGNpcmNsZSBjeD0iMTUxLjA1IiBjeT0iMTUxLjA1IiByPSIxNTEuMDUiIGZpbGw9IiNmZmYiLz48cG9seWdvbiBwb2ludHM9IjE1Ni45IDIzMy44OCAxNDQuMTkgMjMzLjg4IDE0NS42IDY4LjIyIDE1OC4xMSA2OC4yMiAxNTYuOSAyMzMuODgiIGZpbGw9IiNlYzQyOTYiLz48cGF0aCBkPSJNMTQ0LjE5LDIzMy44OGwuMTctMjAuMzNjLTExLjctLjItMjQtMy44OS0zMS04LjkzbDQuODQtMTMuNTJhNTMuODQsNTMuODQsMCwwLDAsMjkuMDYsOC42OGMxNC4zMiwwLDI0LTguMjgsMjQtMTkuNzgsMC0xMS4wOS03Ljg3LTE3Ljk1LTIyLjgtMjQtMjAuNTgtOC4wNy0zMy4zLTE3LjM1LTMzLjMtMzQuOSwwLTE2Ljc1LDExLjkxLTI5LjQ2LDMwLjQ3LTMyLjQ5VjY4LjIyaDEyLjUxVjg3Ljc5YzEyLjExLjQxLDIwLjM4LDMuNjQsMjYuMjMsNy4wN2wtNSwxMy4zMWE0OC44LDQ4LjgsMCwwLDAtMjUuNDMtNi44NmMtMTUuNTMsMC0yMS4zOCw5LjI4LTIxLjM4LDE3LjM2LDAsMTAuNDksNy40NiwxNS43MywyNSwyMywyMC43OCw4LjQ3LDMxLjI3LDE5LDMxLjI3LDM2LjkyLDAsMTUuOTQtMTEuMSwzMC44Ny0zMS44OCwzNC4zdjIxWiIgZmlsbD0iIzQwMzQ4YSIvPjxwb2x5Z29uIHBvaW50cz0iMTQ1LjYxIDY4LjIyIDE1OC4zMSA2OC4yMiAxNTcuODUgMTAzLjg0IDE0NS4zIDEwMy4xNSAxNDUuNjEgNjguMjIiIGZpbGw9IiNlYzQyOTYiLz48cG9seWdvbiBwb2ludHM9IjE1Ni45IDIzMy44OCAxNDQuMTkgMjMzLjg4IDE0NC40OCAxOTkuNjUgMTU3LjE2IDE5OC4yNSAxNTYuOSAyMzMuODgiIGZpbGw9IiNlYzQyOTYiLz48L3N2Zz4=",
                Other: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDIuMSAzMDIuMSI+PGNpcmNsZSBjeD0iMTUxLjA1IiBjeT0iMTUxLjA1IiByPSIxNTEuMDUiIGZpbGw9IiNmZmYiLz48cG9seWdvbiBwb2ludHM9IjE1Ni45IDIzMy44OCAxNDQuMTkgMjMzLjg4IDE0NS42IDY4LjIyIDE1OC4xMSA2OC4yMiAxNTYuOSAyMzMuODgiIGZpbGw9IiNlYzQyOTYiLz48cGF0aCBkPSJNMTQ0LjE5LDIzMy44OGwuMTctMjAuMzNjLTExLjctLjItMjQtMy44OS0zMS04LjkzbDQuODQtMTMuNTJhNTMuODQsNTMuODQsMCwwLDAsMjkuMDYsOC42OGMxNC4zMiwwLDI0LTguMjgsMjQtMTkuNzgsMC0xMS4wOS03Ljg3LTE3Ljk1LTIyLjgtMjQtMjAuNTgtOC4wNy0zMy4zLTE3LjM1LTMzLjMtMzQuOSwwLTE2Ljc1LDExLjkxLTI5LjQ2LDMwLjQ3LTMyLjQ5VjY4LjIyaDEyLjUxVjg3Ljc5YzEyLjExLjQxLDIwLjM4LDMuNjQsMjYuMjMsNy4wN2wtNSwxMy4zMWE0OC44LDQ4LjgsMCwwLDAtMjUuNDMtNi44NmMtMTUuNTMsMC0yMS4zOCw5LjI4LTIxLjM4LDE3LjM2LDAsMTAuNDksNy40NiwxNS43MywyNSwyMywyMC43OCw4LjQ3LDMxLjI3LDE5LDMxLjI3LDM2LjkyLDAsMTUuOTQtMTEuMSwzMC44Ny0zMS44OCwzNC4zdjIxWiIgZmlsbD0iIzQwMzQ4YSIvPjxwb2x5Z29uIHBvaW50cz0iMTQ1LjYxIDY4LjIyIDE1OC4zMSA2OC4yMiAxNTcuODUgMTAzLjg0IDE0NS4zIDEwMy4xNSAxNDUuNjEgNjguMjIiIGZpbGw9IiNlYzQyOTYiLz48cG9seWdvbiBwb2ludHM9IjE1Ni45IDIzMy44OCAxNDQuMTkgMjMzLjg4IDE0NC40OCAxOTkuNjUgMTU3LjE2IDE5OC4yNSAxNTYuOSAyMzMuODgiIGZpbGw9IiNlYzQyOTYiLz48L3N2Zz4=",
                Insurance:
                    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDIuMSAzMDIuMSI+PGNpcmNsZSBjeD0iMTUxLjA1IiBjeT0iMTUxLjA1IiByPSIxNTEuMDUiIGZpbGw9IiNmZmYiLz48cGF0aCBkPSJNNzMuMSwxMDMuMjRjNC4zMy0xLjQ4LDguNzYtMi43OSwxMy00LjQ5QzEwMS41Niw5Mi42MywxMTYsODQuNDksMTMwLDc1LjY1YzYuNTUtNC4xNCwxMy04LjQ5LDE5LjQtMTIuODVhMi4zOSwyLjM5LDAsMCwxLDMuMjUsMGMxNS41NiwxMC44MywzMS41NiwyMC45NCw0OC41NiwyOS4zNyw4Ljc5LDQuMzcsMTcuNzIsOC4zOSwyNy4zMywxMC43YTMuNTIsMy41MiwwLDAsMSwuNTEuMjZjLS41LDMuNzctMS4wNyw3LjU0LTEuNDksMTEuMzItLjk0LDguMzMtMS42OCwxNi42OS0yLjcxLDI1LTIsMTYuMzQtNSwzMi40OC0xMS40OSw0Ny43My02LjUzLDE1LjQ1LTE2Ljg1LDI4LjA2LTI5LjY3LDM4Ljc4YTM1NS44NSwzNTUuODUsMCwwLDEtMzEuNDEsMjRjLS43My40OC0xLjIyLjktMi4yMy4yNGEzMjAuMjksMzIwLjI5LDAsMCwxLTM2LjMxLTI4LjI3Yy0xNi4yNS0xNC41OC0yNi40MS0zMi43OS0zMS40MS01NGEyNzkuMDgsMjc5LjA4LDAsMCwxLTYuMjItMzlDNzUuMjksMTIwLjM1LDc0LjEsMTExLjgxLDczLjEsMTAzLjI0Wk04MS42MiwxMDljMCwuMzMsMCwuNjEuMDYuODksMSw4LjE1LDIuMTksMTYuMjgsMi45NSwyNC40NmEyMzcuNzksMjM3Ljc5LDAsMCwwLDUuNTYsMzMuMjFjNC40NiwxOC41MSwxMy41MywzNC4zMywyNy42OSw0N2EyNzIuNiwyNzIuNiwwLDAsMCwzMS43LDI0LjcyLDIuMTIsMi4xMiwwLDAsMCwyLjgxLDAsMjkzLjcsMjkzLjcsMCwwLDAsMjkuNzktMjMsODguNDEsODguNDEsMCwwLDAsMjUuNDItMzUuNTYsMTQ3LjE2LDE0Ny4xNiwwLDAsMCw3LjkxLTMxLjhjMS4xNS04LDEuODUtMTYsMi43OC0yNCwuNjMtNS4zMiwxLjMtMTAuNjMsMi0xNi04LjgxLTIuMTUtMTcuMDgtNS44Mi0yNS4xOS05Ljg3YTM0NS41MSwzNDUuNTEsMCwwLDEtNDIuNTQtMjUuNzksMi4zMywyLjMzLDAsMCwwLTMuMTEsMGMtOC44NSw1Ljc2LTE3LjYxLDExLjY2LTI2LjY2LDE3LjA2QzEwOS44Miw5OC4wOSw5Ni40OCwxMDUsODEuNjIsMTA5WiIgZmlsbD0iIzQwMzQ4YSIvPjxyZWN0IHg9IjE0NS45NyIgeT0iMTEzLjUyIiB3aWR0aD0iOS45NCIgaGVpZ2h0PSI2Ni45IiBmaWxsPSIjZWM0Mjk2Ii8+PHJlY3QgeD0iMTQ1Ljk3IiB5PSIxMTMuNTIiIHdpZHRoPSI5Ljk0IiBoZWlnaHQ9IjY2LjkiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDMuOTcgMjk3LjkxKSByb3RhdGUoLTkwKSIgZmlsbD0iI2VjNDI5NiIvPjwvc3ZnPg==",
                Car: "data:image/svg+xml;base64,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",
                Motorcycle:
                    "data:image/svg+xml;base64,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",
                Boat: "data:image/svg+xml;base64,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",
                Caravan:
                    "data:image/svg+xml;base64,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",
                Truck: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDIuMTEgMzAyLjExIj48Y2lyY2xlIGN4PSIxNTEuMDUiIGN5PSIxNTEuMDUiIHI9IjE1MS4wNSIgZmlsbD0iI2ZmZiIvPjxwYXRoIGQ9Ik0yMTkuMjYsMTEwLjFIMTk1LjE3YTMuMDksMy4wOSwwLDAsMSwwLTYuMTdoMjQuMDlhMy4wOSwzLjA5LDAsMCwxLDAsNi4xN1oiIGZpbGw9IiM0MDM0OGEiLz48cGF0aCBkPSJNMjY3LjEsMTU4LjI2SDIzMC44MWExNC42NiwxNC42NiwwLDAsMS0xNC42NC0xNC42NFYxMTcuOTNhMy4wOCwzLjA4LDAsMCwxLDYuMTYsMHYyNS42OWE4LjUsOC41LDAsMCwwLDguNDgsOC40OEgyNjcuMWEzLjA4LDMuMDgsMCwwLDEsMCw2LjE2WiIgZmlsbD0iIzQwMzQ4YSIvPjxwYXRoIGQ9Ik0yNjcuNDIsMTgyLjM0YTMuMDgsMy4wOCwwLDAsMS0zLjA4LTMuMDhWMTU3Ljg5YTguNDgsOC40OCwwLDAsMC0uODktMy43OUwyNDcsMTIxLjE3YTE5Ljk0LDE5Ljk0LDAsMCwwLTE3LjkzLTExLjA3aC05Ljc5YTMuMDksMy4wOSwwLDAsMSwwLTYuMTdoOS43OWEyNi4wOSwyNi4wOSwwLDAsMSwyMy40NCwxNC40OGwxNi40OCwzM2ExNC41OCwxNC41OCwwLDAsMSwxLjU1LDYuNTV2MjEuMzVhMy4wNywzLjA3LDAsMCwxLTMuMDcsMy4wOFoiIGZpbGw9IiM0MDM0OGEiLz48cGF0aCBkPSJNMTgzLjYyLDE4Mi4zNGgtNTYuOWEzLjA4LDMuMDgsMCwxLDEsMC02LjE2aDU2LjlhOC40OCw4LjQ4LDAsMCwwLDguNDctOC40OFY5NC40OUE4LjQ4LDguNDgsMCwwLDAsMTgzLjYyLDg2SDYyLjIzYTguNDksOC40OSwwLDAsMC04LjQ4LDguNDhWMTY3LjdhOC40OSw4LjQ5LDAsMCwwLDguNDgsOC40OEg3MWEzLjA4LDMuMDgsMCwxLDEsMCw2LjE2SDYyLjIzQTE0LjY3LDE0LjY3LDAsMCwxLDQ3LjU4LDE2Ny43Vjk0LjQ5QTE0LjY2LDE0LjY2LDAsMCwxLDYyLjIzLDc5Ljg1SDE4My42YTE0LjY1LDE0LjY1LDAsMCwxLDE0LjY1LDE0LjY0VjE2Ny43QTE0LjY0LDE0LjY0LDAsMCwxLDE4My42MiwxODIuMzRaIiBmaWxsPSIjNDAzNDhhIi8+PHBhdGggZD0iTTI1OS43MiwxOTQuMzhIMTk1LjE3YTMuMDgsMy4wOCwwLDAsMSwwLTYuMTZoNjQuNTVhNC42Miw0LjYyLDAsMCwwLDQuNjItNC42MnYtNC4zNGEzLjA5LDMuMDksMCwwLDEsNi4xNywwdjQuMzRBMTAuOCwxMC44LDAsMCwxLDI1OS43MiwxOTQuMzhaIiBmaWxsPSIjNDAzNDhhIi8+PHBhdGggZD0iTTI0OC42OSwxMjFoLTM4LjFhMy4wOCwzLjA4LDAsMCwxLDAtNi4xNmgzOC4xYTMuMDgsMy4wOCwwLDEsMSwwLDYuMTZaIiBmaWxsPSIjNDAzNDhhIi8+PHBhdGggZD0iTTE5NS4xNywxOTQuMzhINTguMzlBMTAuNzksMTAuNzksMCwwLDEsNDcuNiwxODMuNiw3LjQyLDcuNDIsMCwwLDEsNTUsMTc2LjE4SDcxYTMuMDgsMy4wOCwwLDAsMSwwLDYuMTZINTVhMS4yNiwxLjI2LDAsMCwwLTEuMjUsMS4yNiw0LjYyLDQuNjIsMCwwLDAsNC42Myw0LjYySDE5NS4xN2EzLjA4LDMuMDgsMCwwLDEsMCw2LjE2WiIgZmlsbD0iIzQwMzQ4YSIvPjxjaXJjbGUgY3g9IjIxOS4yNiIgY3k9IjE5MS4zIiByPSIxNy4zNCIgZmlsbD0iI2ZmZiIvPjxwYXRoIGQ9Ik0yMTkuMjYsMjExLjcyYTIwLjQyLDIwLjQyLDAsMSwxLDIwLjQyLTIwLjQyaDBBMjAuNDUsMjAuNDUsMCwwLDEsMjE5LjI2LDIxMS43MlptMC0zNC42N2ExNC4yNiwxNC4yNiwwLDEsMCwxNC4yNiwxNC4yNmgwYTE0LjI4LDE0LjI4LDAsMCwwLTE0LjI2LTE0LjI0WiIgZmlsbD0iIzQwMzQ4YSIvPjxjaXJjbGUgY3g9Ijk4Ljg1IiBjeT0iMTkxLjMiIHI9IjE3LjM0IiBmaWxsPSIjZmZmIi8+PHBhdGggZD0iTTk4Ljg1LDIxMS43MmEyMC40MiwyMC40MiwwLDEsMSwyMC40Mi0yMC40MkEyMC40MywyMC40MywwLDAsMSw5OC44NSwyMTEuNzJabTAtMzQuNjdhMTQuMjYsMTQuMjYsMCwxLDAsMTQuMjYsMTQuMjZoMGExNC4yOCwxNC4yOCwwLDAsMC0xNC4yNi0xNC4yNFoiIGZpbGw9IiM0MDM0OGEiLz48cGF0aCBkPSJNMjY3LjQyLDE4Mi4zNEgyNDcuMTNhMy4wOCwzLjA4LDAsMSwxLDAtNi4xNmgyMC4yOWEzLjA4LDMuMDgsMCwxLDEsMCw2LjE2WiIgZmlsbD0iIzQwMzQ4YSIvPjwvc3ZnPg==",
            },
            sortOptions: [
                {
                    title: "Last Updated",
                    value: {
                        name: "updated_at",
                        sorting: true,
                    },
                },
                {
                    title: "Last Stage Change",
                    value: {
                        name: "last_stage_change",
                        sorting: true,
                    },
                },
                {
                    title: "Task Due",
                    value: {
                        name: "tasks.due_date",
                        sorting: true,
                    },
                },
                {
                    title: "Amount",
                    value: {
                        name: "amount",
                        sorting: true,
                    },
                },
                {
                    title: "Customer Name",
                    value: {
                        name: "customer_name",
                        sorting: true,
                    },
                },
            ],
            sortOptionsDirections: [
                {
                    label: "Ascending",
                    type: "ascending",
                },
                {
                    label: "Descending",
                    type: "descending",
                },
            ],
            dateRangeArray: [
                moment().startOf("month"),
                moment().endOf("month")
            ],
            pickerOptions: {
                shortcuts: [
                    {
                        text: 'Today',
                        onClick(picker) {
                            picker.$emit('pick', [moment(), moment()]);
                        }
                    },
                    {
                        text: 'Yesterday',
                        onClick(picker) {
                            picker.$emit('pick', [moment().subtract(1, 'days'), moment().subtract(1, 'days')]);
                        }
                    },
                    {
                        text: 'This Week',
                        onClick(picker) {
                            picker.$emit('pick', [moment().startOf('isoWeek'), moment().endOf('isoWeek')]);
                        }
                    },
                    {
                        text: 'Last Week',
                        onClick(picker) {
                            picker.$emit('pick', [moment().subtract(1, 'week').startOf('isoWeek'), moment().subtract(1, 'isoWeek').endOf('isoWeek')]);
                        }
                    },
                    {
                        text: 'This Month',
                        onClick(picker) {
                            picker.$emit('pick', [moment().startOf('month'), moment().endOf('month')]);
                        }
                    },
                    {
                        text: 'Last Month',
                        onClick(picker) {
                            picker.$emit('pick', [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]);
                        }
                    },
                    {
                        text: 'This Year',
                        onClick(picker) {
                            picker.$emit('pick', [moment().startOf('year'), moment().endOf('year')]);
                        }
                    }
                ]
            },
        }
    },
    computed: {
        ...mapGetters({
            applicationTabView: "userSettings/applicationTabView",
            sortby: "userSettings/sortby",
            getPermissionByName: "user/getPermissionByName",
            applicationCategories: "options/applicationCategories",
            user: "user/user",
        }),
        checked: {
            get() {
                return this.$store.getters["applicationsDashboard/getChecked"]
            },
            set(data) {
                this.$store.dispatch("applicationsDashboard/setChecked", data)
            },
        },
        canViewAllApps() {
            return Ambition.hasRoleOrPermission(["view all applications"])
        },
        canAssignApp() {
            return Ambition.hasRoleOrPermission(["administrator", "assign app"])
        },
        viewUnassigned() {
            return Ambition.hasRoleOrPermission(["administrator", "view unassigned app"])
        },
        mappedApplications() {
            return Object.fromEntries(
                Object.entries(this.applications).map(([key, array]) => [
                    key,
                    array.filter(
                        (data) =>
                            !this.search ||
                            (data.customer_name && data.customer_name.toLowerCase().includes(this.search.toLowerCase()))
                    ),
                ])
            )
        },
        selectedNumber: function () {
            let i = 0
            for (let key in this.checked) {
                if (this.checked.hasOwnProperty(key) && this.checked[key]) {
                    i++
                }
            }
            return i
        },
        viewAsOptionsFiltered() {
            return this.viewAsOptions
        },
        viewAs() {
            return this.applicationTabView
                ? this.applicationTabView
                : isIgnition && !this.isReferrer
                    ? "compact"
                    : !this.isReferrer
                        ? "kanban"
                        : "list"
        },
        assignUserFilter: function () {
            function compare(a, b) {
                if (a.full_name < b.full_name) return -1
                if (a.full_name > b.full_name) return 1
                return 0
            }

            let userList = this.assignUsersInitial.filter(
                (item) => !item.deleted_at && item.active && item.roles.find((role) => role.name == "broker")
            )

            if (this.assignUserSearchInput.length > 0) {
                let reg = new RegExp(this.assignUserSearchInput, "gi")
                userList = userList.filter((item) => item.full_name.match(reg))
            }

            return userList.sort(compare)
        },
        applicationTypesFilter: function () {
            function compare(a, b) {
                if (a.name < b.name) return -1
                if (a.name > b.name) return 1
                return 0
            }

            return this.applicationTypes ? this.applicationTypes.sort(compare) : this.applicationTypes
        },
        assignReferrersInitialFilter: function () {
            function compare(a, b) {
                if (a.name < b.name) return -1
                if (a.name > b.name) return 1
                return 0
            }

            return this.assignReferrersInitial.length ? this.assignReferrersInitial.sort(compare) : this.assignReferrersInitial
        },
        assignReferrerUsersInitialFilter: function () {
            function compare(a, b) {
                if (a.first_name < b.first_name) return -1
                if (a.first_name > b.first_name) return 1
                return 0
            }

            return !_.isEmpty(this.assignReferrerUsersInitial) ? this.assignReferrerUsersInitial.sort(compare) : this.assignReferrerUsersInitial
        },
        totalApps() {
            return Object.values(this.mappedApplications).reduce(function (count, row) {
                return count + row.length
            }, 0)
        },
        checkedApplications() {
            if (_.isEmpty(this.checked)) return []
            return Object.keys(this.checked).map((checkedId) =>
                this.allApplications.find((application) => application.id == checkedId)
            )
        },

    },
    watch: {

        closedApplications(newVal, oldVal){
            if(newVal === false && oldVal === true) {
                this.dateRangeArray = [
                    moment().startOf("month"),
                    moment().endOf("month")
                ]
            }
        },

        filterShowCompleted(newVal, oldVal){
            if(newVal === false && oldVal === true) {
                this.dateRangeArray = [
                    moment().startOf("month"),
                    moment().endOf("month")
                ]
            }
        },

        filterName: {
            immediate: true,
            deep: true,
            handler: _.debounce(function (newVal) {
                if (newVal !== '' && this.savedFilterArray[newVal]) {
                    this.checkFilterName = true
                } else {
                    this.checkFilterName = false
                }
            }, 100)
        },
        applicationTabView(newVal, oldVal) {
            if (newVal) {
                this.$bus.$emit("lead::selectedCounter::reset", true)
                setTimeout(() => {
                    this.loading = false
                }, 2000)
            }
        },
        sortby: {
            immediate: true,
            deep: true,
            handler: _.debounce(function (sortby) {
                if (sortby != this.sortby) {
                    this.$store.dispatch("global commit", {
                        type: "userSettings/setApplicationTabSortBy",
                        payload: sortby,
                    })
                    this.setPreferredSortOptions(sortby)
                }
            }, 1000),
        },
        searchOptions: {
            handler() {
                let tableView = this.$refs.tableView
                if (tableView) tableView.clearSelection()
            },
            deep: true,
            immediate: true,
        },
    },
    created() {
        this.searchOptions.team = _.get(Ambition, "Globals.tenant.user.current_team_id", "all")
        this.closeReasonOptions = this.formatOptions(this.closeReasons)
        this.ambitionSockets = Echo.private("applications_tab." + this.tenant).listen(".refresh", async (e) => {
            if (e.application.application_status_id != 15) {
                let tableApp = null
                let app = null
                if (this.applications[e.application.application_status_id] != undefined) {
                    app = this.applications[_.get(e, "application.application_status_id", 1)].findIndex((app) => {
                        return app.id == e.application.id
                    })
                }

                if (app == -1 || app == null) {
                    // let debounceRefresh = _.debounce(() => {
                    //     this.getApplications()
                    // }, 2000);
                    // debounceRefresh();
                    return
                }

                tableApp = this.allApplications.findIndex((app) => {
                    return app.id == e.application.id
                })

                this.$set(this.applications[_.get(e, "application.application_status_id", 1)], app, e.application)
                this.$set(this.allApplications, tableApp, e.application)
            }
        })
        if (_.get(this.userSettings, "ApplicationSort")) {
            this.$store.dispatch("global commit", {
                type: "userSettings/setApplicationTabSortBy",
                payload: this.userSettings.ApplicationSort,
            })
        }
        if (_.get(this.userSettings, "ApplicationTableSettings")) {
            this.$store.dispatch("global commit", {
                type: "userSettings/setApplicationTableSettings",
                payload: this.userSettings.ApplicationTableSettings,
            })
        }

        this.$store.dispatch("global commit", {
            type: "userSettings/setApplicationTabView",
            payload: this.applicationTabView != "" ? this.applicationTabView : this.viewAs,
        })
    },
    mounted() {
        this.getSavedFilter()
        this.$bus.$on("lead::selectedCounter::reset", (data) => {
            this.$store.dispatch("applicationsDashboard/setChecked", {})
        })
        this.viewAs
        if (this.isReferrer && this.referrerId) {
            this.searchOptions.referrer = this.referrerId
            this.getReferrerUsers(this.referrerId)
        }

        this.getReferrersInital()

        this.getAssignUsers()
        this.getApplications()
        this.getBrokersList()
        this.$store.dispatch("applicationsDashboard/setChecked", {})
        this.storeBrokers(_.get(this.dropDownOptions, "brokers", []), "options/saveBrokers")
        this.storeReferrers(_.get(this.dropDownOptions, "referrers", []), "options/saveReferrers")

        this.storeOptions(_.get(this.dropDownOptions, "employmentStatuses", []), "options/saveEmploymentStatuses")
        this.storeOptions(_.get(this.dropDownOptions, "employmentIndustries", []), "options/saveEmploymentIndustries")
        this.storeOptions(_.get(this.dropDownOptions, "residentialStatuses", []), "options/saveResidentialStatuses")
        this.storeOptions(_.get(this.dropDownOptions, "sellerTypes", []), "options/saveSellerTypes")
        this.storeOptions(_.get(this.dropDownOptions, "applicationJoins", []), "options/saveApplicationJoins")
        this.storeOptions(_.get(this.dropDownOptions, "loanTypes", []), "options/saveLoanTypes")
        this.storeAssetTypes(_.get(this.dropDownOptions, "assetTypes", []), "options/saveAssetTypes")
        this.storeOptions(_.get(this.dropDownOptions, "documents", []), "options/saveDocumentTypes")
        this.storeSources(_.get(this.dropDownOptions, "sources", []), "options/saveSources")
        this.storeOptions(_.get(this.dropDownOptions, "sourceDetails", []), "options/saveSourceDetails")
        this.storeOptions(_.get(this.dropDownOptions, "closeReasons", []), "options/saveCloseReasons")
        this.storeOptions(_.get(this.dropDownOptions, "leadTypes", []), "options/saveLeadTypes")
        this.$store.dispatch("options/saveLenders", _.get(this.dropDownOptions, "lenders", []))
        this.storeOptions(_.get(this.dropDownOptions, "occupations", []), "options/saveOccupations")

        this.$store.dispatch("options/saveApplicationCategories", _.get(this.dropDownOptions, "applicationCategories", []))
        this.$store.dispatch("options/saveEnableLatitudeSubmitToLender", _.get(this.dropDownOptions, "enableLatitudeSubmitToLender", false))
        this.$store.dispatch("options/saveEnableWisrSubmitToLender", _.get(this.dropDownOptions, "enableWisrSubmitToLender", false))
        this.$store.dispatch("options/saveEnableGlaSubmitToLender", _.get(this.dropDownOptions, "enableGlaSubmitToLender", false))
        this.$store.dispatch("options/saveEnableMacquarieSubmitToLender", _.get(this.dropDownOptions, "enableMacquarieSubmitToLender", false))
        this.$store.dispatch("options/saveEnableFirstmacSubmitToLender", _.get(this.dropDownOptions, "enableFirstmacSubmitToLender", false))
        this.$store.dispatch("options/saveEnableNowFinanceSubmitToLender", _.get(this.dropDownOptions, "enableNowFinanceSubmitToLender", false))
        this.$store.dispatch("options/saveEnableMetroSubmitToLender", _.get(this.dropDownOptions, "enableMetroSubmitToLender", false))
        this.$store.dispatch("options/saveEnableAffordableCarLoansSubmitToLender", _.get(this.dropDownOptions, "enableAffordableCarLoansSubmitToLender", false))

        this.$store.dispatch("options/saveApplicationTypes", [
            {label: "Consumer", value: 1},
            {label: "Commercial", value: 2},
        ])

        this.$store.dispatch("options/savePaymentTypes", [
            {label: "Arrears", value: 0},
            {label: "Advance", value: 1},
        ])

        this.$store.dispatch("options/savePreferredRepaymentFrequency", [
            {label: "Weekly", value: "Weekly"},
            {label: "Fortnightly", value: "Fortnightly"},
            {label: "Monthly", value: "Monthly"},
            {label: "Quarterly", value: "Quarterly"},
            {label: "Annual", value: "Annual"},
        ])

        this.storeOptions(_.get(this.dropDownOptions, "repaymentTerms", []), "options/savePreferredRepaymentTerm")
    },
    updated() {
    },
    activated() {
    },
    methods: {
        countDownTimer() {
            if (this.appRefreshTimeout) {
                this.countDownTimerId = setTimeout(() => {
                    const totalSeconds = Math.floor(this.appRefreshTimeout / 1000);
                    if (this.appRefreshSeconds <= 0) {
                        clearTimeout(this.countDownTimerId);
                        this.isFetchingApps = true;
                        this.getApplications();
                    } else {
                        this.appRefreshSeconds -= 1
                        this.countDownTimer()
                        // console.warn('countdown percentage: ', (((totalSeconds - this.appRefreshSeconds) / totalSeconds) * 100))
                    }
                }, 1000)
            }
        },
        getCompleted() {
            if (this.filterShowCompleted) {
                this.closedApplications = false
                this.searchOptions.status = 'completed'
            } else {
                this.searchOptions.status = ""
            }
            this.filter()
        },

        getClosed() {
            if (this.closedApplications) {
                this.filterShowCompleted = false
                this.searchOptions.status = 'closed'
            } else {
                this.searchOptions.status = ""
            }this.filter()
        },

        getDate() {
            // if no date value search all
            if (this.dateRangeArray == [] || this.dateRangeArray == null) {
                this.dateRangeArray = 'all'
                this.searchOptions.dateRange = this.dateRangeArray
            } else {
                // make a copy
                let cp = []
                cp[0] = this.dateRangeArray[0]
                cp[1] = this.dateRangeArray[1]
                // convert to timstamp for date filter in Algolia
                cp[0] = Date.parse(cp[0]) / 1000
                cp[1] = Date.parse(cp[1]) / 1000
                // add to searchOption
                this.searchOptions.dateRange = cp
            }
            this.filter()

        },
        setRefreshTimeout(timer) {
            this.appRefreshTimeout = timer
            this.appRefreshSeconds = Math.floor(timer / 1000)
            this.countDownTimer(timer);
        },
        padTo2Digits(num) {
            return num.toString().padStart(2, '0');
        },

        convertMsToMinutesSeconds(milliseconds) {
            const minutes = Math.floor(milliseconds / 60000);
            const seconds = Math.round((milliseconds % 60000) / 1000);

            return seconds === 60
                ? `${minutes + 1}:00`
                : `${minutes}:${this.padTo2Digits(seconds)}`;
        },

        getBrokersList() {
            axios
                .post(
                    `/api/v2/referrers/get-brokers`,
                    {
                        teamId: Ambition.currentTeamId(),
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.getters.token}`,
                        },
                    }
                )
                .then((res) => {
                    let activeBrokerList = res.data.filter((item) => !item.deleted_at && item.active)
                    let inactiveBrokerList = res.data.filter((item) => (item.deleted_at || !item.active) && item.broker_applications_count > 0)

                    if (!_.isEmpty(activeBrokerList)) {
                        this.brokerList.push({
                            label: "Active Brokers",
                            options: activeBrokerList.map((broker) => {
                                return {
                                    value: broker.id,
                                    label: broker.full_name
                                }
                            }),
                        })
                    }

                    if (!_.isEmpty(inactiveBrokerList)) {
                        this.brokerList.push({
                            label: "Inactive Brokers",
                            options: inactiveBrokerList.map((broker) => {
                                return {
                                    value: broker.id,
                                    label: broker.full_name
                                }
                            }),
                        })
                    }
                })
                .catch((err) => console.error(err))
        },
        closeApplications() {
            if (Ambition.useEmailBuilder()) {
                this.closeApplicationModalVisibility = true
            } else {
                this.closeModal = true
            }
        },
        apiToken() {
            return {
                headers: {Authorization: `Bearer ${this.tokenInitial}`},
            }
        },
        toggleFilters() {
            this.showFilters = !this.showFilters
        },
        getApplications: _.debounce(function () {
            clearTimeout(this.countDownTimerId);
            this.isFetchingApps = true;
            this.$tenant
                .post(
                    `/api/applications/app-search`,
                    {
                        params: this.searchOptions,
                    },
                    {
                        abort: false,
                    }
                )
                .then((res) => {
                    this.applications = res.data.applications
                    console.warn(this.applications)
                    this.allApplications = _.uniqBy(_.flatten(Object.values(res.data.applications)), "ref_id")
                    this.isFetchingApps = false;

                    if (this.appRefreshTimeout) {
                        this.appRefreshSeconds = Math.floor(this.appRefreshTimeout / 1000)
                        this.countDownTimer()
                    }
                    this.loading = false
                })
                .catch((err) => {
                    err.output()
                })
                .finally(() => {
                    this.loading = false
                })
        }, 1000),
        updateStage(update) {
            let stageIndex = this.statuses.findIndex((stage) => {
                return stage.label == update.label
            })
            if (stageIndex > -1) {
                this.statuses[stageIndex].busy = update.busy

                setTimeout(() => {
                    this.statuses[stageIndex].page = update.page
                }, 2000)
            }
        },
        filter() {
            this.loading = true
            let tableView = this.$refs.tableView
            if (tableView) tableView.clearSelection()
            this.getApplications()
        },
        changedReferrer() {
            if (this.searchOptions.referrer) {
                this.filter()
                this.getReferrerUsers(this.searchOptions.referrer)
            }
        },
        createApplication() {
            this.$bus.$emit("create-opp-remote", {})
        },
        async getAssignUsers() {
            try {
                let res = await axios.get(`/api/v2/referrers`, {
                    headers: {
                        Authorization: "Bearer " + this.tokenInitial,
                    },
                })
                this.assignUsersInitial = res.data
            } catch (err) {
                console.error(err)
            }
        },
        async getReferrers(query) {
            try {
                let res = await axios.post(
                    `/admin/referrers/list`,
                    {
                        params: {
                            query: query,
                        },
                    },
                    {
                        headers: {
                            Authorization: "Bearer " + this.tokenInitial,
                        },
                    }
                )
                this.assignReferrersInitial = res.data
                this.loading = false
            } catch (err) {
                console.error(err)
            }
        },
        assign(user) {
            this.assignUser = user
            this.transferTasksModalVisible = true
            this.assignTasks = false

            swal({
                title: "Assign Tasks?",
                text: "Would you like to assign any open tasks with this application",
                type: "info",
                showCancelButton: true,
                cancelButtonText: "Assign Applications Only",
                cancelButtonColor: "#d33",
                confirmButtonText: "Assign Applications & Tasks",
                confirmButtonColor: "#284da5",
                reverseButtons: false,
            }).then((actioned) => {
                if (actioned.value) {
                    this.assignTasks = true
                } else {
                    this.assignTasks = false
                }
                this.commitAssign(user)
            })
        },
        commitAssign(user) {
            if (this.checkSelected()) {
                this.$tenant
                    .put(
                        "/api/applications/assign",
                        {
                            assign_id: user.id,
                            applications: this.checked,
                            assignTasks: this.assignTasks,
                        },
                        {
                            headers: {
                                Authorization: "Bearer " + this.tokenInitial,
                            },
                        }
                    )
                    .then((response) => {
                        let tableView = this.$refs.tableView
                        this.$bus.$emit("lead::selectedCounter::reset", true)

                        if (tableView) tableView.clearSelection()
                        Message({
                            message: "Application assigned successfully.",
                            type: "success",
                        })
                        //refresh table
                        this.filter()
                    })
                    .catch((error) => {
                        Message({
                            message: error.message,
                            type: "error",
                        })
                    })
                    .finally(() => {
                        this.assignTasks = false
                    })
            }
        },
        formatOptions(data) {
            let self = this
            let options = []

            data.map((element) => {
                options.push({label: element.name, value: element.id})
            })

            return options
        },
        deleteSelected() {
            if (this.checkSelected()) {
                this.$tenant
                    .delete("/api/applications/destroy", {
                        data: {
                            applications: this.checked,
                            close_reason: this.closeReason,
                        },
                    })
                    .then((response) => {
                        Message({
                            message: "Application closed successfully.",
                            type: "success",
                        })

                        this.$bus.$emit("lead::selectedCounter::reset", true)
                        this.closeModal = false
                        this.closeReason = ""
                    })
                    .catch((error) => {
                        Message({
                            message: error.message,
                            type: "error",
                        })
                    })
            }
        },
        checkSelected() {
            if (this.selectedNumber > 0) {
                return true
            } else {
                Message({
                    message: "You have to select at least one application.",
                    type: "error",
                })
                return false
            }
        },
        createNote(application) {
            this.noteApplication = application
            this.openNoteModal = true
        },
        handleViewChange(val) {
            this.loading = true
            this.$store.dispatch("global commit", {type: "userSettings/setApplicationTabView", payload: val})
        },
        handleClear() {
            this.getApplications()
        },
        clearSaveFilterInput() {
            this.filterName = ''
        },
        clearSelectedSavedFilter() {
            this.selectedSavedFilter = ''
            this.searchOptions.applicationType = null
            this.searchOptions.assignedUser = null
            this.searchOptions.referrer = null
            this.searchOptions.referrer_user = null
            this.searchOptions.application_categories = []
            this.sortby.prop = {}
            this.sortby.order = ''

            this.getApplications()
        },
        handleSelectedSavedFilter() {
            let savedFilterForUser = this.savedFilterArray[this.selectedSavedFilter]

            if (savedFilterForUser) {
                this.searchOptions.applicationType = savedFilterForUser.type
                this.searchOptions.assignedUser = savedFilterForUser.assigned
                this.searchOptions.referrer = savedFilterForUser.referrer
                this.searchOptions.referrer_user = savedFilterForUser.referrerUser
                this.searchOptions.application_categories = savedFilterForUser.categories
                this.sortby.prop = savedFilterForUser.sortBy
                this.sortby.order = savedFilterForUser.order
            }

            this.getApplications()
        },
        getSavedFilter() {
            this.$tenant.get("/api/v2/user-settings/getAppFilters").then((res) => {
                console.log('getAppFilters ', res.data)
                this.savedFilterArray = res.data
            }).catch(err => {
                err.output()
            })
        },
        saveFilter() {
            let userId = this.user.id
            let filterName = this.filterName

            if (filterName && (this.searchOptions.applicationType || this.searchOptions.assignedUser || this.searchOptions.referrer || this.searchOptions.referrer_user || this.searchOptions.application_categories || this.sortby.prop || this.sortby.order)) {
                const appFilter = {
                    [filterName]: {
                        type: this.searchOptions.applicationType,
                        assigned: this.searchOptions.assignedUser,
                        referrer: this.searchOptions.referrer,
                        referrerUser: this.searchOptions.referrer_user,
                        categories: this.searchOptions.application_categories,
                        sortBy: this.sortby.prop,
                        order: this.sortby.order
                    }
                }

                this.$tenant.put("/api/v2/user-settings", {'applicationsLists': {[userId]: appFilter}})
                    .then(res => {
                        this.savedFilterArray = res.data.applicationsLists[userId];
                        this.filterName = ''
                    }).catch(err => {
                    err.output()
                })
            }
        },
        getReferrersInital() {
            if (!this.assignReferrersInitial.length) {
                this.getReferrers("")
            }
        },
        async getReferrers(query) {
            try {
                let res = await this.$tenant.post(`/admin/referrers/list`, {
                    params: {
                        query: query,
                    },
                })
                this.assignReferrersInitial = res.data
                this.loading = false
            } catch (err) {
                console.error(err)
            }
        },
        async getReferrerUsers(referrer) {
            try {
                let res = await this.$tenant.post(`/admin/referrers/list/${referrer}`, {
                    params: {
                        referrer: referrer,
                    },
                })
                this.assignReferrerUsersInitial = res.data
                this.loading = false
            } catch (err) {
                console.error(err)
            }
        },
        setDashboard(option) {
            this.$tenant.put("/api/v2/user-settings", {dashboard: option}).then((res) => {
                if (option == "old") {
                    location.reload()
                }
            })
        },
        setPreferredSortOptions(options) {
            this.$tenant
                .put("/api/v2/user-settings", {
                    ApplicationSort: options,
                })
                .then((res) => {
                })
        },
        storeOptions: function (data, action) {
            let optionList = []

            data.map((element) => {
                optionList.push({
                    label: element.name,
                    value: element.id,
                    name: _.camelCase(element.name),
                })
            })
            this.$store.dispatch(action, optionList)
        },
        storeSources(data, action) {
            let optionList = []
            data.map((element) => {
                optionList.push({
                    label: _.get(element, "label"),
                    weight: _.get(element, "weight"),
                    value: _.get(element, "value"),
                    detail: _.get(element, "detail"),
                })
            })
            this.$store.dispatch(action, optionList)
        },
        storeAssetTypes(data, action) {
            let optionList = []
            data.map((element) => {
                optionList.push({
                    label: _.get(element, "label", _.get(element, "name")),
                    name: _.get(element, "label", _.get(element, "name")),
                    value: _.get(element, "id", _.get(element, "value")),
                    categories: _.get(element, "asset_categories"),
                })
            })
            this.$store.dispatch(action, optionList)
        },
        storeReferrers: function (data, action) {
            let optionList = []
            data.map((element) => {
                element.users.map((user) => {
                    optionList.push({
                        label: `${element.name} - ${user.first_name} ${user.last_name}`,
                        value: user.id,
                        teamID: element.team_id,
                    })
                })
            })
            this.$store.dispatch(action, optionList)
        },
        storeBrokers: function (data, action) {
            let optionList = []
            data.map((element) => {
                const disabled = element.active === 0
                optionList.push({
                    label: `${element.first_name} ${element.last_name}${disabled ? " (Deactivated)" : ""}`,
                    value: element.id,
                    teams: _.map(element.teams, "id"),
                    disabled,
                })
            })
            this.$store.dispatch(action, optionList)
        },
    },
}
</script>
<style lang="scss">
.warning {
    .el-link--inner {
        color: #e6a23c;
    }
}

.note {
    &.el-button--warning {
        background-color: #fbff00;
        border-color: #e6a23c;
        color: #e6a23c;

        &:hover {
            background: #ebb563;
            border-color: #ebb563;
            color: #fff;
        }
    }
}
</style>
<style lang="scss" scoped>
.category-select {
    .el-select-dropdown__item {
        padding: 0 10px 0 20px !important;
    }

    .category-item {
        width: 50px;
        min-height: 34px;
        display: flex;
        align-items: center;
        justify-content: center;

        .category-dot {
            width: 10px;
            height: 10px;
            border-radius: 10px;
        }
    }
}

.slide-enter-active {
    -moz-transition-duration: 0.3s;
    -webkit-transition-duration: 0.3s;
    -o-transition-duration: 0.3s;
    transition-duration: 0.3s;
    -moz-transition-timing-function: ease-in;
    -webkit-transition-timing-function: ease-in;
    -o-transition-timing-function: ease-in;
    transition-timing-function: ease-in;
}
</style>
